import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useCopy, useLocale } from '../../content'
import { SERVICE_KEYS } from '../../data/services'
import { pathFor } from '../../routes'

/* ── Types ────────────────────────────────────────────────────────── */
/** One card's visuals; its title and description are the service's `name` / `short` in the dictionary. */
interface Servizio {
  num: string
  icon: ReactNode
  video?: string
}

/* ── Inline icons ─────────────────────────────────────────────────── */
const LeafIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 2c1 2 2 4.5 2 8 0 5.5-4.5 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
)
const ScissorsIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
    <line x1="20" y1="4" x2="8.12" y2="15.88" />
    <line x1="14.47" y1="14.48" x2="20" y2="20" />
    <line x1="8.12" y1="8.12" x2="12" y2="12" />
  </svg>
)
const RulerIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.4 2.4 0 0 1 0-3.4l2.6-2.6a2.4 2.4 0 0 1 3.4 0Z" />
    <path d="m14.5 12.5 2-2" /><path d="m11.5 9.5 2-2" />
    <path d="m8.5 6.5 2-2" /><path d="m17.5 15.5 2-2" />
  </svg>
)
const TreeIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22V10" />
    <path d="M7 14a5 5 0 0 1-2-9.5 5.5 5.5 0 0 1 11-1A5 5 0 0 1 17 14H7Z" />
  </svg>
)
/** Scopa: handle, binding band, bristle fan — drawn upright, leaned 30°. */
const BroomIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <g transform="translate(2 -1) rotate(30 12 12)">
      <path d="M12 2v9" />
      <path d="M9 11h6v2.5H9z" />
      <path d="M9 13.5 6.5 21.5h11L15 13.5" />
      <path d="M11 14l-.7 7.5M13 14l.7 7.5" />
    </g>
  </svg>
)

/* ── Nav arrows ───────────────────────────────────────────────────── */
const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
)
const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
)

/* ── Service data (order = SERVICE_KEYS) ──────────────────────────── */
const SERVIZI: Servizio[] = [
  {
    num: '01',
    icon: <RulerIcon />,
    video: '/media/verde-vercelli/servizi/reel_01-noaudio.mp4',
  },
  {
    num: '02',
    icon: <TreeIcon />,
    video: '/media/verde-vercelli/servizi/reel_02-noaudio.mp4',
  },
  {
    num: '03',
    icon: <LeafIcon />,
    video: '/media/verde-vercelli/servizi/reel_03-noaudio.mp4',
  },
  {
    num: '04',
    icon: <BroomIcon />,
    video: '/media/verde-vercelli/servizi/reel_04-noaudio.mp4',
  },
  {
    num: '05',
    icon: <ScissorsIcon />,
    video: '/media/verde-vercelli/servizi/reel_05-noaudio.mp4',
  },
  {
    num: '06',
    icon: <TreeIcon />,
    video: '/media/verde-vercelli/servizi/reel_06-noaudio.mp4',
  },
  {
    num: '07',
    icon: <TreeIcon />,
    video: '/media/verde-vercelli/treeclimbing.mp4',
  },
  {
    num: '08',
    icon: <ScissorsIcon />,
    video: '/media/verde-vercelli/servizi/reel_07-noaudio.mp4',
  },
]

const N = SERVIZI.length
const ANGLE_STEP = (2 * Math.PI) / N

/* ── Helpers: compute card style from its angle offset ────────────── */
function getCardStyle(offset: number) {
  const x = Math.sin(offset)
  const z = Math.cos(offset)

  const scale = 0.5 + 0.5 * Math.max(0, z)             // 0.5 (back) → 1.0 (front)
  const translateX = x * 380                             // wider spread
  const opacity = Math.max(0, 0.2 + 0.8 * z)
  const zIndex = Math.round((z + 1) * 100)

  return { scale, translateX, opacity, zIndex }
}

/* ── Carousel ─────────────────────────────────────────────────────── */
export function Servizi() {
  const [active, setActive] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const locale = useLocale()
  const copy = useCopy()
  const dragStartX = useRef(0)
  const dragDelta = useRef(0)
  const isDragging = useRef(false)
  const autoTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const goTo = useCallback((i: number) => {
    setActive(((i % N) + N) % N)
  }, [])
  const next = useCallback(() => setActive(a => ((a + 1) % N + N) % N), [])
  const prev = useCallback(() => setActive(a => ((a - 1) % N + N) % N), [])

  const pauseAutoRotate = useCallback(() => {
    setIsPaused(true)
    clearTimeout(resumeTimer.current)
    resumeTimer.current = setTimeout(() => setIsPaused(false), 3000)
  }, [])

  // Auto-rotate only for cards without video (video cards advance via onEnded)
  useEffect(() => {
    const hasVideo = !!SERVIZI[active]?.video
    if (isPaused || hasVideo) { clearTimeout(autoTimer.current); return }
    autoTimer.current = setTimeout(next, 5000)
    return () => clearTimeout(autoTimer.current)
  }, [active, isPaused, next])

  // Keyboard
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') { next(); pauseAutoRotate() }
      if (e.key === 'ArrowLeft') { prev(); pauseAutoRotate() }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [next, prev, pauseAutoRotate])

  // Pointer drag
  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return
    isDragging.current = true
    dragStartX.current = e.clientX
    dragDelta.current = 0
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    pauseAutoRotate()
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return
    dragDelta.current = e.clientX - dragStartX.current
  }
  const onPointerUp = () => {
    if (!isDragging.current) return
    isDragging.current = false
    if (dragDelta.current > 60) prev()
    else if (dragDelta.current < -60) next()
    dragDelta.current = 0
  }

  return (
    <section
      id="servizi"
      className="relative bg-brand-forest-dark text-brand-cream
                 px-5 sm:px-8 overflow-hidden
                 min-h-[100dvh] flex flex-col justify-center py-10 sm:py-14"
    >
      {/* Topographic bg */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none"
        viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true"
      >
        <defs>
          <pattern id="topo-servizi" x="0" y="0" width="600" height="400" patternUnits="userSpaceOnUse">
            <g fill="none" stroke="rgb(245 237 224)" strokeWidth="0.6">
              <path d="M 0 200 Q 150 140, 300 200 T 600 200" />
              <path d="M 0 240 Q 150 180, 300 240 T 600 240" />
              <path d="M 0 280 Q 150 220, 300 280 T 600 280" />
              <path d="M 0 320 Q 150 260, 300 320 T 600 320" />
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#topo-servizi)" />
      </svg>

      <div className="relative max-w-6xl mx-auto w-full">
        {/* ── Title ──────────────────────────────────────── */}
        <div className="text-center mb-2 relative z-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="w-10 h-px bg-brand-brass-bright" />
            <span className="mono-label text-brand-brass-bright">{copy.servicesSection.eyebrow}</span>
            <span className="w-10 h-px bg-brand-brass-bright" />
          </div>
          <h2
            className="font-display text-brand-cream text-3xl sm:text-4xl lg:text-5xl leading-tight"
            style={{ letterSpacing: '0.02em', fontWeight: 400 }}
          >
            {copy.servicesSection.title}
          </h2>
        </div>

        {/* ── Carousel stage ─────────────────────────────── */}
        <div
          className="relative select-none touch-pan-y overflow-hidden isolate"
          style={{ height: 'calc(100dvh - 220px)', maxHeight: 550, zIndex: 1 }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => {
            clearTimeout(resumeTimer.current)
            resumeTimer.current = setTimeout(() => setIsPaused(false), 1500)
          }}
        >
          {SERVIZI.map((s, i) => {
            // Angular offset from front position
            let offset = (i - active) * ANGLE_STEP
            // Normalize to [-PI, PI]
            while (offset > Math.PI) offset -= 2 * Math.PI
            while (offset < -Math.PI) offset += 2 * Math.PI

            const { scale, translateX, opacity, zIndex } = getCardStyle(offset)
            // Prerender-safe floats: identical on server and client, and no zero runs
            // (check-prerender rule 3 reads eleven zeros as a placeholder phone).
            const r3 = (n: number) => Math.round(n * 1000) / 1000
            const isFront = i === active
            const page = copy.pages[SERVICE_KEYS[i]]

            return (
              <div
                key={s.num}
                className="absolute left-1/2 w-[280px] sm:w-[300px]"
                style={{
                  top: '50%',
                  height: '92%',
                  transform: `translate(calc(-50% + ${r3(translateX)}px), -50%) scale(${r3(scale)})`,
                  opacity: r3(opacity),
                  zIndex,
                  transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  cursor: isFront ? 'default' : 'pointer',
                  pointerEvents: opacity < 0.1 ? 'none' : 'auto',
                }}
                onClick={() => {
                  if (!isFront) { goTo(i); pauseAutoRotate() }
                }}
              >
                <article
                  className={`w-full h-full rounded-2xl overflow-hidden border
                              flex flex-col transition-colors duration-600
                              ${isFront
                                ? 'border-brand-brass/40 shadow-[0_20px_60px_-15px_rgba(201,168,117,0.25)]'
                                : 'border-brand-cream/10'
                              }`}
                  style={{
                    background: isFront
                      ? 'linear-gradient(180deg, rgba(1,50,32,0.5) 0%, rgba(245,237,224,0.08) 100%)'
                      : 'rgba(245,237,224,0.04)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  {/* ── Visual area ─── */}
                  <div className="relative flex-[5] flex items-center justify-center overflow-hidden min-h-0">
                    {s.video ? (
                      <CardVideo src={s.video} isActive={isFront} onEnded={next} />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center relative"
                        style={{
                          background: 'linear-gradient(160deg, rgba(1,50,32,0.6) 0%, rgba(10,31,21,0.85) 100%)',
                        }}
                      >
                        <svg
                          className="absolute inset-0 w-full h-full opacity-10"
                          viewBox="0 0 260 220" preserveAspectRatio="xMidYMid slice" aria-hidden="true"
                        >
                          <g fill="none" stroke="rgb(245 237 224)" strokeWidth="0.5">
                            <path d="M 0 60 Q 65 30, 130 60 T 260 60" />
                            <path d="M 0 90 Q 65 60, 130 90 T 260 90" />
                            <path d="M 0 130 Q 65 100, 130 130 T 260 130" />
                            <path d="M 0 160 Q 65 130, 130 160 T 260 160" />
                          </g>
                        </svg>
                        <div className={`relative transition-all duration-500 ${
                          isFront ? 'text-brand-brass-bright scale-110' : 'text-brand-cream/50'
                        }`}>
                          {s.icon}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Text area ─── */}
                  <div className="flex-[3] px-5 py-4 flex flex-col gap-2 justify-center">
                    <h3
                      className="font-display text-brand-cream text-base sm:text-lg leading-snug"
                      style={{ letterSpacing: '0.01em', fontWeight: 400 }}
                    >
                      <Link
                        to={pathFor(SERVICE_KEYS[i], locale)}
                        onClick={(e) => { if (!isFront) e.preventDefault() }}
                        className="hover:underline decoration-brand-brass-bright underline-offset-4"
                      >
                        {page.name}
                      </Link>
                    </h3>
                    <div
                      className={`h-px bg-brand-brass-bright origin-left transition-transform duration-500 ${
                        isFront ? 'scale-x-100' : 'scale-x-0'
                      }`}
                      style={{ width: '2rem' }}
                    />
                    <p className="text-brand-cream/55 text-xs leading-relaxed line-clamp-4">
                      {page.short}
                    </p>
                  </div>
                </article>
              </div>
            )
          })}
        </div>

        {/* ── Navigation ─────────────────────────────────── */}
        <div className="relative z-10 flex items-center justify-center gap-6 mt-4">
          <button
            onClick={() => { prev(); pauseAutoRotate() }}
            className="w-10 h-10 rounded-full border border-brand-cream/20 flex items-center justify-center
                       text-brand-cream/60 hover:text-brand-brass-bright hover:border-brand-brass-bright/50
                       transition-all duration-300 hover:scale-110"
            aria-label={copy.a11y.carouselPrev}
          >
            <ChevronLeft />
          </button>

          <div className="flex items-center gap-2">
            {SERVIZI.map((_, i) => (
              <button
                key={i}
                onClick={() => { goTo(i); pauseAutoRotate() }}
                className={`rounded-full transition-all duration-300 ${
                  active === i
                    ? 'w-6 h-2 bg-brand-brass-bright'
                    : 'w-2 h-2 bg-brand-cream/25 hover:bg-brand-cream/50'
                }`}
                aria-label={`${copy.a11y.carouselGoTo} ${copy.pages[SERVICE_KEYS[i]].name}`}
              />
            ))}
          </div>

          <button
            onClick={() => { next(); pauseAutoRotate() }}
            className="w-10 h-10 rounded-full border border-brand-cream/20 flex items-center justify-center
                       text-brand-cream/60 hover:text-brand-brass-bright hover:border-brand-brass-bright/50
                       transition-all duration-300 hover:scale-110"
            aria-label={copy.a11y.carouselNext}
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </section>
  )
}

/* ── Video sub-component (play/pause, advance on end) ─────────────── */
function CardVideo({ src, isActive, onEnded }: { src: string; isActive: boolean; onEnded: () => void }) {
  const ref = useRef<HTMLVideoElement>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ref.current) return
    if (isActive) {
      setLoading(true)
      ref.current.currentTime = 0
      ref.current.play().catch(() => {})
    } else {
      ref.current.pause()
      ref.current.currentTime = 0.5
    }
  }, [isActive])

  return (
    <div className="relative w-full h-full">
      <video
        ref={ref}
        src={src}
        muted
        playsInline
        onEnded={onEnded}
        onCanPlayThrough={() => setLoading(false)}
        onPlaying={() => setLoading(false)}
        className="w-full h-full object-cover"
      />
      {loading && isActive && (
        <div className="absolute inset-0 flex items-center justify-center bg-brand-forest-dark/60">
          <svg className="w-8 h-8 animate-spin text-brand-brass-bright" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  )
}
